import React, {useEffect, useState} from "react";
import {useParams} from "react-router";
import {FaPlus} from "react-icons/fa";
import {Template} from "../../../components/template/template.jsx";
import {Card} from "../../../components/card/card.jsx";
import Alert from "../../../components/alert/alert.jsx";
import Button from "../../../components/button/button.jsx";
import InputForm from "../../../components/input/input-form.jsx";
import AbsenceTypeService from "../../../services/web/absence-type-service.js";
import {navigateHook} from "../../../hooks/use-navigate.js";
import projectUtils from "../../../utils/project-utils.js";

const ERROR_MESSAGES = {
    "absence.type.not.found": "Motivo de ausência não encontrado.",
    "absence.type.name.required": "O campo \"Nome\" é obrigatório.",
    "absence.type.name.duplicate": "Este nome já está sendo utilizado.",
    "absence.type.in.use": "Não é possível excluir este motivo de ausência porque ele está sendo utilizado."
};

const AbsenceTypeForm = () => {
    const {id} = useParams();
    const navigate = navigateHook();
    const [type, setType] = useState({name: ""});
    const [isInUse, setIsInUse] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!id) {
            setType({name: ""});
            setIsInUse(false);
            return;
        }

        const loadForm = async () => {
            try {
                setIsLoading(true);
                const [absenceType, usage] = await Promise.all([
                    AbsenceTypeService.get(id),
                    AbsenceTypeService.isInUse(id)
                ]);
                setType(absenceType);
                setIsInUse(usage.inUse);
            } catch (error) {
                projectUtils.swalErrorMessage("", "Não foi possível carregar o motivo de ausência.", "Ok");
            } finally {
                setIsLoading(false);
            }
        };

        loadForm();
    }, [id]);

    const error = (e) => {
        const key = e.response?.data?.i18nMessage;
        projectUtils.swalErrorMessage("", ERROR_MESSAGES[key] ?? "Não foi possível salvar.", "Ok");
    };

    const save = async () => {
        try {
            setIsLoading(true);
            const saved = id ? await AbsenceTypeService.update(id, type) : await AbsenceTypeService.create(type);
            projectUtils.success(!id);
            if (!id) navigate(`/absence-types/form/${saved.id}`, false);
        } catch (e) {
            error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const destroy = () => {
        projectUtils.swalMessage("warning", "Excluir", `Excluir o motivo de ausência "${type.name}"?`, "Sim", true, "Não", async ({isConfirmed}) => {
            if (!isConfirmed) return;

            try {
                await AbsenceTypeService.delete(id);
                projectUtils.success(true, "Excluído com sucesso.");
                navigate("/absence-types", false);
            } catch (e) {
                error(e);
            }
        });
    };

    return <Template isLoading={isLoading}>
        <Template.Header>
            <Template.Title>Motivo de ausência</Template.Title>
            {id && <Button target="primary-outline" onClick={() => navigate("/absence-types/form", false)}><FaPlus/> Adicionar motivo</Button>}
        </Template.Header>
        <Template.Body>
            <Card>
                <Card.Body>
                    <InputForm id="name" label="Nome *" value={type.name} onChange={({target}) => setType({name: target.value})}/>
                </Card.Body>
            </Card>
            <Card className="mt-2">
                <Card.Body className="flex justify-end gap-2 items-center">
                    {isInUse && <Alert target="warning">Não é possível excluir este motivo de ausência porque ele está sendo utilizado.</Alert>}
                    {id && <Button target="danger" disabled={isInUse} onClick={destroy}>Excluir</Button>}
                    <Button onClick={save}>Salvar</Button>
                </Card.Body>
            </Card>
        </Template.Body>
    </Template>;
};

export default AbsenceTypeForm;
